/**
 * Bubble Type Definitions
 * Sprite-based bubble system with JSON configuration
 */

import bubbleConfigData from '@/config/bubbleConfig.json';

/**
 * Sound configuration for bubble pop
 */
export interface BubbleSoundConfig {
  key: string;
  path: string;
  volume: number;
}

/**
 * Bubble configuration interface
 */
export interface BubbleConfig {
  name: string;
  sprite: string;
  assetPath: string;
  explosionColors: string[];
  basePoints: {
    large: number;
    medium: number;
    small: number;
    tiny: number;
  };
  tint: string;
  alpha: number;
  blendMode: string;
  /** v2 shader dual-tone gradient — inner core colour */
  innerColor?: string;
  /** v2 shader dual-tone gradient — rim/outer colour */
  rimColor?: string;
  /** v2 shader additive glow colour */
  glowColor?: string;
  glowEnabled?: boolean; // Optional: enable FX glow on bubble
  /** Legacy glow colour — renamed in v2 to avoid collision with shader glowColor */
  glowColor_fx?: string;
  glowStrength?: number; // Optional: glow intensity (default: 4)
  glowPulse?: boolean; // Optional: animate glow pulsing (default: false)
  glowPulseSpeed?: number; // Optional: pulse animation speed in ms (default: 800)
  glowPulseMin?: number; // Optional: minimum glow strength during pulse (default: 2)
  glowPulseMax?: number; // Optional: maximum glow strength during pulse (default: glowStrength * 1.5)
  /** Pollution-type hazard data */
  isPollution?: boolean;
  pollutionDuration?: number;
  pollutionRadius?: number;
}

/** Parse "0xRRGGBB" into normalized [r,g,b] (0..1). */
export function hexToRGB01(hex: string | undefined, fallback: [number, number, number] = [0.3, 0.9, 1.0]): [number, number, number] {
  if (!hex) return fallback;
  const n = parseInt(hex.replace('0x', ''), 16);
  if (Number.isNaN(n)) return fallback;
  return [((n >> 16) & 0xff) / 255, ((n >> 8) & 0xff) / 255, (n & 0xff) / 255];
}

/**
 * Bubble configuration map
 */
export interface BubbleConfigMap {
  [key: string]: BubbleConfig;
}

/**
 * Load bubble configurations from JSON
 */
const bubbleConfigs: BubbleConfigMap = bubbleConfigData.bubbles as BubbleConfigMap;

/**
 * Global bubble system configuration
 */
export const BUBBLE_TEXTURE_RADIUS = bubbleConfigData.textureRadius as number;
export const BUBBLE_GROW_MULTIPLIER = bubbleConfigData.growMultiplier as number;
export const BUBBLE_POP_MULTIPLIER = bubbleConfigData.popMultiplier as number;

/**
 * Bubble sound configurations by size
 */
const bubbleSounds = bubbleConfigData.sounds as Record<string, BubbleSoundConfig>;

/**
 * Export bubble types for easy access
 */
export const BUBBLE_TYPES = bubbleConfigs;

/**
 * Available bubble type names
 */
export type BubbleTypeName = keyof typeof bubbleConfigs;

/**
 * Helper to get bubble config from type name
 */
export function getBubbleConfig(typeName: BubbleTypeName = 'REGULAR'): BubbleConfig {
  return bubbleConfigs[typeName] || bubbleConfigs['REGULAR'];
}

/**
 * Helper to get explosion colors as number array
 */
export function getExplosionColors(typeName: BubbleTypeName = 'REGULAR'): number[] {
  const config = getBubbleConfig(typeName);
  return config.explosionColors.map(color => parseInt(color, 16));
}

/**
 * Helper to get tint as number
 */
export function getBubbleTint(typeName: BubbleTypeName = 'REGULAR'): number {
  const config = getBubbleConfig(typeName);
  return parseInt(config.tint, 16);
}

/**
 * Get all available bubble type names
 */
export function getAllBubbleTypes(): BubbleTypeName[] {
  return Object.keys(bubbleConfigs) as BubbleTypeName[];
}

/**
 * Get sound configuration for a bubble size
 */
export function getBubbleSound(size: 'large' | 'medium' | 'small' | 'tiny'): BubbleSoundConfig {
  return bubbleSounds[size];
}

/**
 * Get all unique bubble sound configurations for loading
 */
export function getAllBubbleSounds(): BubbleSoundConfig[] {
  const uniqueSounds = new Map<string, BubbleSoundConfig>();
  Object.values(bubbleSounds).forEach(sound => {
    uniqueSounds.set(sound.key, sound);
  });
  return Array.from(uniqueSounds.values());
}

/**
 * Determine bubble size category from radius
 */
export function getBubbleSizeFromRadius(radius: number): 'large' | 'medium' | 'small' | 'tiny' {
  if (radius >= 128) return 'large';
  if (radius >= 64) return 'medium';
  if (radius >= 32) return 'small';
  return 'tiny';
}
